import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useForm } from "react-hook-form";
import useLipaBidhaaStore from "../../../stores/lipaBidhaaStore";
import { useGetMerchantWalletBalance } from "../../../hooks/queries/useMerchantQueries";
import {
  useLipaBidhaaCharges,
  useLipaBidhaaPayment,
  useLipaBidhaaResendOtp,
  useLipaBidhaaTransaction,
  useLipaBidhaaVerification,
} from "../../../hooks/queries/useLipaBidhaaQueries";
import { usePollTransaction } from "./usePollTransaction";
import { useOtpInactivityTimer } from "../../shared/useOtpInactivityTimer";
import { useResendTimer } from "../../../hooks/useResendTimer";
import { useSetFieldError } from "../../../hooks/useSetFieldError";
import { ALIAS_VERIFICATION_STATUS } from "../config/verificationStatus";
import {
  PAYMENT_SUMMARY_DETAILS,
  PAYMENT_TYPE_CONFIG,
  PAYMENT_TYPE_FIELD_CONFIG,
  PAYMENT_TYPE_HEADER,
  PAYMENT_TYPES,
  LIPA_BIDHAA_ACTION_CODES,
  LIPA_BIDHAA_PIN_ERROR_CODES,
  TRANSACTION_NOT_FOUND_MESSAGE,
  TERMINAL_TRANSACTION_STATUSES,
} from "../config/lipaBidhaaConfig";
import { formatKES, getDisplayValue } from "../../../utils/transactionutils";
import { toastService } from "../../../providers";
import { extractAxiosErrorMessage } from "../../../services/utils/errorUtils";
import {
  isInvalidOtpMessage,
  applyOtpBlock,
  applyPinLockedBlock,
} from "../../shared/otpErrorUtils";
import {
  useLipaBidhaaOtpBlockStore,
  usePinLockedStore,
} from "../../../stores/useOtpBlockStore";
import {
  parsePinCode,
  formatPinInvalidMessage,
} from "../../../services/utils/pinAuthUtils";
import { authQueryKeys } from "../../../hooks/queries/useAuthQueries";

function getMerchantVerificationStatus(aliasVerificationStatus) {
  if (aliasVerificationStatus === ALIAS_VERIFICATION_STATUS.VERIFIED)
    return "success";
  if (aliasVerificationStatus === ALIAS_VERIFICATION_STATUS.UNVERIFIED)
    return "error";
  return undefined;
}

function buildTransactionPayload({
  paymentType,
  alias,
  accountNumber,
  amount,
}) {
  const base = { amount: Number(amount), paymentType };
  switch (paymentType) {
    case PAYMENT_TYPES.PAYBILL:
      return { ...base, paybill: alias, accountNumber };
    case PAYMENT_TYPES.TILL_NUMBER:
      return { ...base, tillNumber: alias };
    case PAYMENT_TYPES.B2B:
      return { ...base, alias };
    default:
      return base;
  }
}

function getVerifyErrorMessage(paymentType) {
  const label =
    PAYMENT_TYPE_FIELD_CONFIG[paymentType]?.aliasLabel ?? "merchant";
  return `Please verify your ${label.toLowerCase()} number before continuing.`;
}

function buildSummaryCardProps({
  paymentTypeLabel,
  aliasLabel,
  liveAlias,
  businessName,
  merchantVerificationStatus,
  formattedAmount,
  formattedTransactionFee,
  formattedWalletBalance,
  currentStep,
  paymentRail,
}) {
  if (currentStep === 1) {
    return {
      title: PAYMENT_SUMMARY_DETAILS.paymentSummary.title,
      bannerMessage: PAYMENT_SUMMARY_DETAILS.paymentSummary.bannerMessage,
      summaryItems: [
        {
          label: "Payment Type",
          value: paymentTypeLabel,
        },
        {
          label: aliasLabel,
          value: liveAlias || "—",
          status: merchantVerificationStatus,
        },
        {
          label: "Business Name",
          value: businessName || "—",
          status: merchantVerificationStatus,
        },
        {
          label: "Amount",
          value: formattedAmount,
        },
        {
          label: "Transaction Fee",
          value: formattedTransactionFee,
        },
        {
          label: "Current Balance",
          value: formattedWalletBalance,
        },
      ],
    };
  }
  return {
    title: PAYMENT_SUMMARY_DETAILS.transactionDetails.title,
    bannerMessage: PAYMENT_SUMMARY_DETAILS.transactionDetails.bannerMessage,
    summaryItems: [
      { label: "Type", value: paymentTypeLabel },
      { label: "Recipient", value: businessName || "—", status: "success" },
      { label: "Payment Rail", value: paymentRail },
      { label: "Processing Time", value: "Instant" },
    ],
  };
}

export function usePaymentFlow(paymentType) {
  const {
    currentStep,
    aliasVerificationStatus,
    businessName,
    transactionFee,
    otpRecipient,
    confirmationMode,
    transactionUuid,
    accountNumber: storedAccountNumberForResult,
    verifiedAlias,
    walletBalance: storedWalletBalance,
    remarks,
    transactionAmount: draftTransactionAmount,
  } = useLipaBidhaaStore(
    useShallow(
      ({
        currentStep,
        aliasVerificationStatus,
        businessName,
        transactionFee,
        otpRecipient,
        confirmationMode,
        transactionUuid,
        accountNumber,
        verifiedAlias,
        walletBalance,
        remarks,
        transactionAmount,
      }) => ({
        currentStep,
        aliasVerificationStatus,
        businessName,
        transactionFee,
        otpRecipient,
        confirmationMode,
        transactionUuid,
        accountNumber,
        verifiedAlias,
        walletBalance,
        remarks,
        transactionAmount,
      }),
    ),
  );

  const {
    alias: storedAlias,
    accountNumber: storedAccountNumber,
    transactionAmount: storedTransactionAmount,
  } = useLipaBidhaaStore.getState();

  const { watchFields } = PAYMENT_TYPE_FIELD_CONFIG[paymentType] ?? {
    watchFields: ["alias", "amount"],
  };

  const { control, trigger, watch, setError, setFocus, clearErrors } = useForm({
    defaultValues: {
      alias: storedAlias || "",
      accountNumber: storedAccountNumber || "",
      amount: storedTransactionAmount || "",
    },
    mode: "onTouched",
  });

  const setFieldError = useSetFieldError(setError, setFocus);

  useEffect(() => {
    if (aliasVerificationStatus === ALIAS_VERIFICATION_STATUS.VERIFIED) {
      clearErrors("alias");
    }
  }, [aliasVerificationStatus, clearErrors]);

  const {
    data: walletData,
    isLoading: isBalanceLoading,
    isError: isBalanceFetchError,
  } = useGetMerchantWalletBalance();
  // Null signals "not yet available" — keeps AmountField validation disabled
  // until a real value is confirmed.
  const walletBalance = walletData?.balance ?? null;
  const isBalanceError = !isBalanceLoading && isBalanceFetchError;

  const { mutate: fetchCharges, isPending: isLoadingCharges } =
    useLipaBidhaaCharges();

  const { mutate: submitPayment, isPending: isRequestingPayment } =
    useLipaBidhaaPayment();

  const { mutate: verifyPayment, isPending: isAuthorizing } =
    useLipaBidhaaVerification();

  const { mutate: resendOtp } = useLipaBidhaaResendOtp();

  const {
    secondsLeft: resendSecondsLeft,
    startTimer: startResendTimer,
    isActive: isResendCooldownActive,
  } = useResendTimer(60);

  const { isPolling, startPolling } = usePollTransaction();

  // True from the very first render whenever we land on step 3 with an OTP
  // verify attempt already submitted (e.g. after a refresh mid-request or
  // mid-polling) — set via a lazy initializer so there's no flash of the
  // interactive OTP form before the rehydration check below resolves.
  const [isRehydrating, setIsRehydrating] = useState(() => {
    const state = useLipaBidhaaStore.getState();
    return (
      state.currentStep === 3 &&
      !!state.transactionUuid &&
      state.otpAuthorizationSubmitted
    );
  });

  // Fetches on Step 4 (data not needed until the result screen) and while
  // rehydrating step 3 after a refresh, to confirm the real transaction
  // status before resuming polling or showing the OTP form.
  const {
    data: transactionData,
    isLoading: isLoadingTransaction,
    isError: isTransactionError,
    refetch: refetchTransaction,
  } = useLipaBidhaaTransaction(
    currentStep >= 4 || isRehydrating ? transactionUuid : null,
    { enablePolling: false },
  );

  useEffect(() => {
    if (!transactionData) return;
    if (!isRehydrating) return;
    setIsRehydrating(false);
    if (TERMINAL_TRANSACTION_STATUSES.has(transactionData.status)) {
      useLipaBidhaaStore.getState().setCurrentStep(4);
    } else {
      startPolling();
    }
  }, [transactionData, isRehydrating, startPolling]);

  const {
    control: otpControl,
    handleSubmit: handleOtpSubmit,
    watch: watchOtp,
  } = useForm({ defaultValues: { otp: "" } });

  const otpValue = watchOtp("otp") ?? "";

  const {
    control: pinControl,
    handleSubmit: handlePinSubmit,
    watch: watchPin,
    setError: setPinError,
    reset: resetPinForm,
  } = useForm({ defaultValues: { pin: "" } });

  const pinValue = watchPin("pin") ?? "";

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleOtpTimeout = useCallback(() => {
    navigate("/lipa-bidhaa", {
      state: { lipaBidhaaBannerReason: "otp_session_expired" },
    });
  }, [navigate]);

  const { resetTimer: resetOtpTimer } = useOtpInactivityTimer({
    otpValue: confirmationMode === "PIN" ? pinValue : otpValue,
    isAuthorizing,
    isPolling,
    isRehydrating,
    isActive: currentStep === 3,
    onTimeout: handleOtpTimeout,
  });

  // Step 3 is "busy" (PIN input/submit disabled) for any of these reasons;
  // kept separate from the overlay's visibility condition, which has an
  // additional exception for the rehydration-error fallback screen.
  const isStepThreeBusy = isAuthorizing || isPolling || isRehydrating;

  const liveAlias = watch("alias");
  const liveAmount = watch("amount");
  const liveAccountNumber = watch("accountNumber");

  const paymentTypeLabel = getDisplayValue(
    PAYMENT_TYPE_HEADER[paymentType]?.title,
  );
  const aliasLabel =
    PAYMENT_TYPE_FIELD_CONFIG[paymentType]?.aliasLabel ?? "Reference";
  const merchantVerificationStatus = getMerchantVerificationStatus(
    aliasVerificationStatus,
  );
  const paymentRail = getDisplayValue(PAYMENT_TYPE_CONFIG[paymentType]?.rail);
  const formattedAmount = formatKES(liveAmount);
  const formattedTransactionFee = formatKES(transactionFee);
  const formattedWalletBalance = formatKES(walletBalance);

  const summaryCardProps = buildSummaryCardProps({
    paymentTypeLabel,
    aliasLabel,
    liveAlias,
    businessName,
    merchantVerificationStatus,
    formattedAmount,
    formattedTransactionFee,
    formattedWalletBalance,
    currentStep,
    paymentRail,
  });

  const handleStep1Next = async () => {
    const isValid = await trigger(watchFields);
    if (!isValid) return;

    if (aliasVerificationStatus !== ALIAS_VERIFICATION_STATUS.VERIFIED) {
      setFieldError("alias", getVerifyErrorMessage(paymentType));
      return;
    }

    fetchCharges(
      buildTransactionPayload({
        paymentType,
        alias: liveAlias,
        accountNumber: liveAccountNumber,
        amount: liveAmount,
      }),
      {
        onSuccess: (data) => {
          useLipaBidhaaStore.getState().setTransactionCharges({
            transactionFee: data.totalCharge,
            totalAmount: data.totalAmount,
          });
          useLipaBidhaaStore.getState().setWalletBalance(data.newBalance);
          useLipaBidhaaStore.getState().setCurrentStep(2);
        },
        onError: (error) => {
          toastService.error(extractAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleStep2Back = () => {
    useLipaBidhaaStore.getState().setCurrentStep(1);
  };

  const handleStep2Next = () => {
    const { verifiedAlias, accountNumber, transactionAmount } =
      useLipaBidhaaStore.getState();

    submitPayment(
      buildTransactionPayload({
        paymentType,
        alias: verifiedAlias,
        accountNumber,
        amount: transactionAmount,
      }),
      {
        onSuccess: (data) => {
          const nextConfirmationMode =
            data.action?.code === LIPA_BIDHAA_ACTION_CODES.PIN_CONFIRMATION_REQUIRED
              ? "PIN"
              : "OTP";
          useLipaBidhaaStore.getState().setTransactionUuid(data.id);
          useLipaBidhaaStore.getState().setOtpRecipient(data.phoneNumber);
          useLipaBidhaaStore.getState().setConfirmationMode(nextConfirmationMode);
          startResendTimer();
          useLipaBidhaaStore.getState().setCurrentStep(3);
        },
        onError: () => {
          toastService.error("Unable to initiate payment. Please try again.");
        },
      },
    );
  };

  const handleResendOtp = () => {
    const { transactionUuid: uuid } = useLipaBidhaaStore.getState();
    if (!uuid) return;
    resetOtpTimer();
    startResendTimer();
    resendOtp(uuid, {
      onError: () => {
        toastService.error("Failed to resend code. Please try again.");
      },
    });
  };

  const handlePinAuthorizeSubmit = ({ pin }) => {
    useLipaBidhaaStore.getState().setOtpAuthorizationSubmitted(true);
    verifyPayment(
      { transactionId: transactionUuid, pin },
      {
        onSuccess: (data) => {
          const code = parsePinCode(null, data);

          if (code === LIPA_BIDHAA_PIN_ERROR_CODES.PIN_INVALID) {
            useLipaBidhaaStore.getState().setOtpAuthorizationSubmitted(false);
            resetPinForm({ pin: "" });
            setPinError("pin", { message: formatPinInvalidMessage(data) });
            return;
          }

          if (code === LIPA_BIDHAA_PIN_ERROR_CODES.PIN_LOCKED) {
            applyPinLockedBlock(
              data,
              usePinLockedStore,
              LIPA_BIDHAA_PIN_ERROR_CODES.PIN_LOCKED,
            );
            queryClient.invalidateQueries({
              queryKey: authQueryKeys.currentUser(),
            });
            navigate("/lipa-bidhaa");
            return;
          }

          if (code === LIPA_BIDHAA_PIN_ERROR_CODES.PIN_SETUP_REQUIRED) {
            toastService.error(
              "Your PIN is not set up. Please contact support.",
            );
            navigate("/lipa-bidhaa");
            return;
          }

          queryClient.invalidateQueries({
            queryKey: authQueryKeys.currentUser(),
          });
          useLipaBidhaaStore.getState().setTransactionResult(data);
          startPolling();
        },
        onError: (err) => {
          if (!err.response) {
            useLipaBidhaaStore.getState().setOtpAuthorizationSubmitted(false);
            toastService.error(
              "Request failed. Please check your connection and try again.",
            );
            return;
          }

          const code = parsePinCode(err, null);

          if (code === TRANSACTION_NOT_FOUND_MESSAGE) {
            toastService.error(
              "This payment request is no longer valid. Please start again.",
            );
            navigate("/lipa-bidhaa");
            return;
          }

          toastService.error(extractAxiosErrorMessage(err));
        },
      },
    );
  };

  const handleOtpAuthorizeSubmit = ({ otp }) => {
    useLipaBidhaaStore.getState().setOtpAuthorizationSubmitted(true);
    verifyPayment(
      { otp },
      {
        onSuccess: (data) => {
          useLipaBidhaaStore.getState().setTransactionResult(data);
          startPolling();
        },
        onError: (err) => {
          if (!err.response) {
            useLipaBidhaaStore.getState().setOtpAuthorizationSubmitted(false);
            toastService.error(
              "Request failed. Please check your connection and try again.",
            );
            return;
          }
          const message = extractAxiosErrorMessage(err);
          if (isInvalidOtpMessage(message)) {
            useLipaBidhaaStore.getState().setOtpAuthorizationSubmitted(false);
            toastService.error(
              "Invalid code entered. Please check and try again.",
            );
            return;
          }
          if (message.toLowerCase().includes("validation blocked")) {
            useLipaBidhaaStore.getState().setOtpAuthorizationSubmitted(false);
            applyOtpBlock(err.response, useLipaBidhaaOtpBlockStore);
            navigate("/lipa-bidhaa");
            return;
          }
          useLipaBidhaaStore.getState().setTransactionFailed(message);
          useLipaBidhaaStore.getState().setCurrentStep(4);
        },
      },
    );
  };

  const handleStep3Submit =
    confirmationMode === "PIN"
      ? handlePinAuthorizeSubmit
      : handleOtpAuthorizeSubmit;

  return {
    currentStep,
    control,
    walletBalance,
    isBalanceError,
    handleStep1Next,
    handleStep2Back,
    handleStep2Next,
    isRequestingPayment,
    summaryCardProps,
    isLoadingCharges,
    otpControl,
    handleOtpSubmit,
    otpValue,
    otpRecipient,
    pinControl,
    handlePinSubmit,
    pinValue,
    confirmationMode,
    handleStep3Submit,
    isAuthorizing,
    handleResendOtp,
    resendSecondsLeft,
    isResendCooldownActive,
    isPolling,
    isRehydrating,
    isStepThreeBusy,
    transactionUuid,
    transactionData,
    isLoadingTransaction,
    isTransactionError,
    refetchTransaction,
    paymentType,
    businessName,
    accountNumber: storedAccountNumberForResult,
    verifiedAlias,
    walletBalanceSnapshot: storedWalletBalance,
    remarks,
    draftTransactionAmount,
    draftTransactionFee: transactionFee,
  };
}
